import React, { useEffect} from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import SVG from 'react-inlinesvg';
import shareScreenIcon from '@lifesize/ux-assets/vectors/svgIcons/share-screen.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import classes from 'components/InCall/CallFooter.module.scss';
import { START_PRESENTATION_BUTTON, STOP_PRESENTATION_BUTTON } from 'constants/lsqaConstants';
import { nucleusConstants } from '@lifesize/nucleus';
import { constants as c } from '@lifesize/clients.sdk';
import { logger } from 'utils/logger';

const messages = defineMessages({
  shareScreen: {
    id: 'components.incall.controls.screenShare.tooltip.share',
    description: 'Default share screen tooltip',
    defaultMessage: 'Share your screen'
  },
  currentPresenter: {
    id: 'components.incall.controls.screenShare.tooltip.presenter',
    description: 'Name of current presenter',
    defaultMessage: '{name} is presenting'
  },
  presentationRestricted: {
    id: 'components.incall.controls.screenShare.tooltip.restricted',
    description: 'presentation restricted tooltip',
    defaultMessage: 'Only moderators, lecturers, and owners can share in this call.'
  }
});

export const PresentationButton = (props) => {
  const {
    isRemotePresentationExists,
    canPresent,
    conferenceType,
    isConnected,
    isPresenting,
    isDisabled,
    showPresentationSelection,
    intl,
    mediaState,
    presenterName,
    onStopPresentationClick,
    presentationRestrictionFlagEnabled,
    isModerator,
    isOwner,
    isLecturer,
    isPresentationRestricted
  } = props;

  const showPresentationRestrictionTooltip = () => {
     return conferenceType === nucleusConstants.GALAXY_CONFERENCE_TYPE_VMR && !canPresent && !isRemotePresentationExists;
  };

  const inactivePresentationTooltip = () => {
    // check for presentationRestrictionEnabled before setting tooltip.
    if(showPresentationRestrictionTooltip()) {
      return intl.formatMessage(messages.presentationRestricted);
    } else if (presenterName){
      return intl.formatMessage(messages.currentPresenter, { name: presenterName });
    } else {
      return intl.formatMessage(messages.shareScreen);
    }
  }

  useEffect(() => {
    if (presentationRestrictionFlagEnabled && isPresenting && !isModerator && !isOwner && !isLecturer && isPresentationRestricted) {
      try{
        onStopPresentationClick();
      } catch (e) {
        logger.error(`Error occurred while stopping presentation ${JSON.stringify(e)}`);
      }
    }
  }, [
    isModerator,
    presentationRestrictionFlagEnabled,
    isPresenting,
    onStopPresentationClick,
    isPresentationRestricted,
    isOwner,
    isLecturer,
    mediaState.presentationToken
  ]);

  useEffect(() => {
    if(mediaState.presentationToken === c.PRESENTATION_TOKEN_STATE_DENIED){
      try{
        logger.warn('stopping presentation because presentation token is denied');
        onStopPresentationClick();
      } catch (e) {
        logger.error(`Error occurred while stopping presentation after${JSON.stringify(e)}`);
      }
    }
  }, [
    mediaState.presentationToken,
    onStopPresentationClick
  ])


  let button = null;
  if (isConnected) {
    if (isPresenting) {
      button = (
        <button
          className={classes.inCallButtonPresentationActive}
          onClick={onStopPresentationClick}
          {...STOP_PRESENTATION_BUTTON}
          data-testid="stop-presentation-btn"
        >
          <SVG src={shareScreenIcon} />
          <Tooltip
            location="top"
            style={{ bottom: '100%', marginBottom: 15 }} label={(
              <FormattedMessage
                id="components.incall.controls.screenShare.tooltip.notShare"
                defaultMessage="Stop sharing your screen"
              />
            )}
          />
        </button>
      );
    } else {
      button = (
        <button
          className={`${classes.inCallButton} ${isDisabled ? classes.disabled : ''}`}
          disabled={isDisabled}
          onClick={() => showPresentationSelection()}
          {...START_PRESENTATION_BUTTON}
          data-testid="start-presentation-btn"
        >
          <SVG src={shareScreenIcon} />
          <Tooltip
            location="top"
            style={{ bottom: '100%', marginBottom: 15 }} label={inactivePresentationTooltip()}
          />
        </button>
      );
    }
  }
  return button;
}

PresentationButton.propTypes = {
  isConnected: PT.bool.isRequired,
  intl: PT.object,
  presenterName: PT.string,
  isCallConnected: PT.string,
  isPresenting: PT.bool.isRequired,
  isDisabled: PT.bool.isRequired,
  onStopPresentationClick: PT.func.isRequired,
  showPresentationSelection: PT.func.isRequired,
  isPresentationRestricted: PT.bool.isRequired,
  presentationRestrictionFlagEnabled: PT.bool.isRequired,
  conferenceType: PT.string.isRequired,
  isModerator: PT.string.isRequired,
  isOwner: PT.string.isRequired,
  isLecturer: PT.string.isRequired,
  mediaState: PT.object.isRequired
};

export default injectIntl(PresentationButton);
